import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import { bugoStateSelectorFamily } from '@shared/api/bugo/bugo.selector';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  AdditionalOption,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import {
  GiftOrderCreateDto,
  GiftOrderDeliveryMode,
  GiftSelectedOption,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { StoreUserMenuButton } from '@shared/components/StoreUserMenuButton';
import { TextField } from '@shared/components/TextField';
import { useAuth } from '@shared/state/hooks/useAuth';
import { CheckOutState } from '@shared/interfaces/bootpay.interface';
import { formatPrice } from '@shared/utils/formatPrice';
import { getGiftAgentOrder } from '@shared/utils/giftAgentOrder.util';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import { GiftCheckoutItemCard } from '../components/GiftCheckoutItemCard';
import GiftDeliveryModeSelector from '../components/GiftDeliveryModeSelector';
import { GiftPrivacyNotice } from '../components/GiftPrivacyNotice';
import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { GiftThanksPrintSummary } from '../containers/GiftThanksPrintSection/GiftThanksPrintSummary';
import { useGiftCheckoutPayment } from '../hooks/useGiftCheckoutPayment';
import { useGiftDeliveryAddressMemory } from '../hooks/useGiftDeliveryAddressMemory';
import { giftCartAtom } from '../state/giftCart.atom';
import { giftThanksPrintForBugoSelector } from '../state/giftThanksPrint.atom';
import {
  calcGiftOrderPayment,
  getGiftOrderDto,
  OrderItemInput,
} from '../utils/getGiftOrderDto';
import { resolveAllowIndividualDelivery } from '../utils/resolveAllowIndividualDelivery';
import { getUnitSurcharge, resolveShippingFeeConfig } from '../utils/resolveDeliveryFee';
import { scrollToInvalidField } from '../utils/scrollToInvalidField';
import {
  buildThanksPrintDto,
  resolveOrderThanksPrint,
} from '../utils/thanksPrintMessage';

const GiftCheckoutPage: FC = function GiftCheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const [cartItems, setCartItems] = useRecoilState(giftCartAtom);
  //* 이 상주(부고)로 작성된 감사인쇄 입력값만 사용 — 다른 상주 응대 중 남은 값 차단
  const thanksSelection = useRecoilValue(giftThanksPrintForBugoSelector(bugoId));

  // bugo 데이터 조회
  useBugoHook(bugoId ?? '', !bugoId);
  const { data: bugoData } = useRecoilValue(bugoStateSelectorFamily(bugoId ?? ''));

  // 로그인한 게스트(StoreGuest) 프로필 — 주문자 정보 prefill에 사용
  const { userProfile } = useAuth();

  // 상품 목록 조회 (장바구니 아이템의 상세 정보를 위해)
  useGiftItemListForStoreHook(bugoId ?? '');
  const giftItemListStatus = useRecoilValue(
    giftItemListForStoreStatusAtomFamily(bugoId ?? ''),
  );
  const giftItemListData = useRecoilValue(
    giftItemListForStoreDataAtomFamily(bugoId ?? ''),
  );

  useEffect(() => {
    setTitle('주문/결제');
  }, [setTitle]);

  // 현재 bugoId에 해당하는 장바구니 아이템만 필터링
  const filteredCartItems = useMemo(() => {
    if (!bugoId) return cartItems;
    return cartItems.filter((item) => item.bugoId === bugoId);
  }, [cartItems, bugoId]);

  // 주문자 정보 (member 정보 기반) — 대리주문 모드면 상주 이름·연락처를 미리 채운다
  const [receiverName, setReceiverName] = useState(
    () => getGiftAgentOrder(bugoId ?? undefined)?.memberName ?? '',
  );
  const [receiverTel, setReceiverTel] = useState(() =>
    (getGiftAgentOrder(bugoId ?? undefined)?.memberPhone ?? '').replace(/[^0-9]/g, ''),
  );

  // 미입력 alert 시 스크롤 타겟
  const receiverNameRef = useRef<HTMLInputElement>(null);
  const receiverTelRef = useRef<HTMLInputElement>(null);
  const addressButtonRef = useRef<HTMLButtonElement>(null);

  // 배송 방식 관련 상태
  const [deliveryMode, setDeliveryMode] = useState<GiftOrderDeliveryMode>(
    GiftOrderDeliveryMode.Bulk,
  );
  const [bulkReceiverAddress, setBulkReceiverAddress] = useState('');
  const [bulkReceiverPostalCode, setBulkReceiverPostalCode] = useState('');
  const [bulkReceiverAddressDetail, setBulkReceiverAddressDetail] = useState('');

  // 배송형 상품 포함 여부
  const hasDeliveryItem = useMemo(() => {
    return filteredCartItems.some((cartItem) => {
      const giftItemDetail = giftItemListData?.find(
        (item) => item._id === cartItem.itemId,
      );
      return giftItemDetail?.giftItemInfo?.type !== GiftItemInfoType.Coupon;
    });
  }, [filteredCartItems, giftItemListData]);

  // 개별배송 허용 여부 — 배송형 상품이 모두 개별배송을 허용해야 가능 (하나라도 불가면 일괄배송 전용)
  const allowIndividual = useMemo(() => {
    return filteredCartItems.every((cartItem) => {
      const giftItemDetail = giftItemListData?.find(
        (item) => item._id === cartItem.itemId,
      );
      const info = giftItemDetail?.giftItemInfo;
      if (!info || info.type === GiftItemInfoType.Coupon) return true; // 쿠폰형은 배송과 무관
      return resolveAllowIndividualDelivery(info);
    });
  }, [filteredCartItems, giftItemListData]);

  // 감사 메시지 인쇄 — 주문 전체에 "하나로 통일" 적용. (바로구매와 동일한 공용 리졸버 사용)
  const orderThanksPrint = useMemo(
    () =>
      resolveOrderThanksPrint(
        filteredCartItems.map((cartItem) => ({
          itemId: cartItem.itemId,
          giftItemInfo: giftItemListData?.find((gi) => gi._id === cartItem.itemId)
            ?.giftItemInfo,
        })),
        thanksSelection?.brandId,
      ),
    [filteredCartItems, giftItemListData, thanksSelection?.brandId],
  );

  // 개별배송 불가 상품이 섞이면 일괄배송(Bulk)으로 강제
  useEffect(() => {
    if (!allowIndividual && deliveryMode === GiftOrderDeliveryMode.Individual) {
      setDeliveryMode(GiftOrderDeliveryMode.Bulk);
    }
  }, [allowIndividual, deliveryMode]);

  // 최근 배송지 기억(localStorage) — 결제 실패/새로고침에도 배송지 보존 + 칩으로 재적용
  const {
    saved: savedAddress,
    applySaved: applySavedAddress,
    showChip: showSavedAddressChip,
    clear: clearSavedAddress,
    canClear: canClearAddress,
  } = useGiftDeliveryAddressMemory({
    address: bulkReceiverAddress,
    postalCode: bulkReceiverPostalCode,
    addressDetail: bulkReceiverAddressDetail,
    setAddress: setBulkReceiverAddress,
    setPostalCode: setBulkReceiverPostalCode,
    setAddressDetail: setBulkReceiverAddressDetail,
    enabled: hasDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk,
    bugoId,
  });

  // 주문자 정보 초기값 — 로그인한 게스트(StoreGuest) 본인이 실제 주문자이므로 userProfile.info 우선,
  // 없을 때만 bugo 상주(member) 정보로 폴백. 데이터가 처음 도착했을 때 1회만 시딩
  // (창 포커스 refetch 재실행 시 사용자가 고친 입력값을 덮어쓰지 않도록)
  const receiverSeededRef = useRef(false);
  useEffect(() => {
    // 대리주문 모드: 주문자는 상주로 고정(초기값 유지) — 로그인 프로필(관리자)로
    // 덮어쓰면 관리자 이름·번호로 게스트가 만들어지고 입금안내 문자도 관리자에게
    // 가는 사고가 된다
    if (bugoId && getGiftAgentOrder(bugoId)) return;
    if (receiverSeededRef.current) return;
    const name = userProfile?.info?.name || bugoData?.member?.fullName || '';
    const phone = userProfile?.info?.phoneNumber || bugoData?.member?.phoneNumber || '';
    if (!name && !phone) return;
    receiverSeededRef.current = true;
    if (name) setReceiverName(name);
    if (phone) setReceiverTel(phone);
  }, [userProfile, bugoData, bugoId]);

  // 같은 상품이라도 옵션 조합이 다르면 별도 라인 → itemId + 옵션 시그니처로 식별
  const optionSignature = useCallback(
    (opts?: { optionName: string; slotIndex?: number; choiceName: string }[]) =>
      (opts ?? [])
        .map((o) => `${o.optionName}#${o.slotIndex ?? 0}:${o.choiceName}`)
        .join('|'),
    [],
  );

  // 수량 증가
  const increaseQuantity = useCallback(
    (itemId: string, sig: string, orderUnit: number) => {
      setCartItems((prev) =>
        prev.map((item) =>
          item.itemId === itemId && optionSignature(item.selectedOptions) === sig
            ? { ...item, quantity: item.quantity + orderUnit }
            : item,
        ),
      );
    },
    [setCartItems, optionSignature],
  );

  // 수량 감소
  const decreaseQuantity = useCallback(
    (itemId: string, sig: string, minOrderQuantity: number, orderUnit: number) => {
      setCartItems((prev) =>
        prev.map((item) => {
          if (item.itemId === itemId && optionSignature(item.selectedOptions) === sig) {
            const newQty = item.quantity - orderUnit;
            return newQty >= minOrderQuantity ? { ...item, quantity: newQty } : item;
          }
          return item;
        }),
      );
    },
    [setCartItems, optionSignature],
  );

  // 옵션 변경 — 같은 itemId의 해당 라인(oldSig)의 selectedOptions를 교체.
  // 변경 결과가 기존 다른 라인과 같은 조합이면 수량을 합치고 편집 라인은 제거한다.
  const changeOptions = useCallback(
    (itemId: string, oldSig: string, next: GiftSelectedOption[]) => {
      const newSig = optionSignature(next);
      if (newSig === oldSig) return;
      setCartItems((prev) => {
        const edited = prev.find(
          (i) => i.itemId === itemId && optionSignature(i.selectedOptions) === oldSig,
        );
        if (!edited) return prev;
        const dup = prev.find(
          (i) => i.itemId === itemId && optionSignature(i.selectedOptions) === newSig,
        );
        if (dup) {
          return prev
            .map((i) =>
              i === dup ? { ...i, quantity: i.quantity + edited.quantity } : i,
            )
            .filter(
              (i) =>
                !(i.itemId === itemId && optionSignature(i.selectedOptions) === oldSig),
            );
        }
        return prev.map((i) =>
          i.itemId === itemId && optionSignature(i.selectedOptions) === oldSig
            ? { ...i, selectedOptions: next.length > 0 ? next : undefined }
            : i,
        );
      });
    },
    [setCartItems, optionSignature],
  );

  // 주문 라인 — 장바구니 단가(스냅샷)는 유지하되 소량주문 추가금/과세 판정은 최신 상세정보(브랜드 포함) 사용.
  // 바로구매와 동일한 OrderItemInput 형태로 만들어 결제금액 계산·DTO 생성을 공용 유틸로 통일한다.
  const orderItems = useMemo<OrderItemInput[]>(
    () =>
      filteredCartItems.map((cartItem) => {
        const detail = giftItemListData?.find((gi) => gi._id === cartItem.itemId);
        const giftItem = (
          detail
            ? { ...detail, priceRetail: cartItem.priceRetail }
            : { _id: cartItem.itemId, priceRetail: cartItem.priceRetail }
        ) as GiftItemForStore;
        return {
          giftItem,
          quantity: cartItem.quantity,
          selectedOptions: cartItem.selectedOptions,
        };
      }),
    [filteredCartItems, giftItemListData],
  );

  // 결제금액(상품+옵션+소량주문 추가금, 과세/면세/부가세) — 공용 계산기로 표시가=청구가 보장
  const payment = useMemo(() => calcGiftOrderPayment(orderItems), [orderItems]);
  const finalTotal = payment.paymentByCash;

  // GiftOrderCreateDto 생성 함수 — 공용 getGiftOrderDto로 일원화 (바로구매와 동일 경로)
  const createOrderDto = useCallback((): GiftOrderCreateDto | null => {
    if (!bugoData || filteredCartItems.length === 0 || giftItemListStatus !== 'success') {
      return null;
    }

    const isBulk = hasDeliveryItem && deliveryMode === GiftOrderDeliveryMode.Bulk;

    return getGiftOrderDto({
      bugo: bugoData,
      receiverName,
      receiverPhoneNumber: receiverTel,
      items: orderItems,
      deliveryMode: hasDeliveryItem ? deliveryMode : undefined,
      bulkDeliveryAddress: isBulk
        ? {
            name: receiverName,
            phoneNumber: receiverTel,
            address: bulkReceiverAddress,
            addressDetail: bulkReceiverAddressDetail || undefined,
            postalCode: bulkReceiverPostalCode,
          }
        : undefined,
      // 감사메시지 인쇄 — 인쇄 대상 상품 + 작성된 내용이 있을 때만 (주문당 1건)
      thanksPrint: orderThanksPrint
        ? buildThanksPrintDto(
            thanksSelection,
            orderThanksPrint.config,
            orderThanksPrint.brandId,
            orderThanksPrint.allowedTemplateIds,
          )
        : undefined,
    });
  }, [
    bugoData,
    filteredCartItems.length,
    giftItemListStatus,
    orderItems,
    receiverName,
    receiverTel,
    hasDeliveryItem,
    deliveryMode,
    bulkReceiverAddress,
    bulkReceiverAddressDetail,
    bulkReceiverPostalCode,
    orderThanksPrint,
    thanksSelection,
  ]);

  // 결제 성공 시 장바구니 비우기
  const handlePaymentSuccess = useCallback(() => {
    const purchasedItemIds = filteredCartItems.map((item) => item.itemId);
    setCartItems((prev) =>
      prev.filter((item) => !purchasedItemIds.includes(item.itemId)),
    );
  }, [filteredCartItems, setCartItems]);

  // Bulk 배송 시 배송지 유효성 검증
  const validateDeliveryAddress = useCallback(() => {
    if (!hasDeliveryItem || deliveryMode !== GiftOrderDeliveryMode.Bulk) return true;
    if (!bulkReceiverAddress.trim()) {
      alert('배송지 주소를 입력해주세요.');
      scrollToInvalidField(addressButtonRef.current);
      return false;
    }
    return true;
  }, [hasDeliveryItem, deliveryMode, bulkReceiverAddress]);

  // 결제 훅 사용
  const { checkoutState, handlePayment, isProcessing } = useGiftCheckoutPayment({
    createOrderDto,
    onPaymentSuccess: handlePaymentSuccess,
    bugoId,
    receiverName,
    receiverTel,
    hasItems: filteredCartItems.length > 0,
    validateDeliveryAddress,
    receiverNameRef,
    receiverTelRef,
  });

  // 장바구니가 비어있으면 리다이렉트
  useEffect(() => {
    if (filteredCartItems.length === 0 && checkoutState === CheckOutState.IDLE) {
      // 약간의 딜레이 후 리다이렉트 (초기 로딩 시 오작동 방지)
      const timer = setTimeout(() => {
        if (filteredCartItems.length === 0) {
          navigate(`/gift-store/cart?bugoId=${bugoId}`);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filteredCartItems, navigate, bugoId, checkoutState]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <StoreUserMenuButton floatingTopClassName="top-16" />
      {/* 1. 주문자 정보 */}
      <div className="bg-white p-4">
        <h3 className="mb-3 font-bold">주문자 정보</h3>
        <div className="space-y-3">
          <TextField
            ref={receiverNameRef}
            scrollOnFocus={true}
            className="w-full"
            placeholder="주문자 성함"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
          />
          <TextField
            ref={receiverTelRef}
            scrollOnFocus={true}
            type="tel"
            className="w-full"
            placeholder="휴대전화번호"
            value={phoneNumberStringFormator(receiverTel)}
            maxLength={13}
            onChange={(e) => setReceiverTel(e.target.value.replace(/[^0-9]/gi, ''))}
          />
        </div>
      </div>

      {/* 2. 구매 상품 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">
          구매 상품 {filteredCartItems.length > 1 && `(${filteredCartItems.length}개)`}
        </h3>
        <div className="space-y-3">
          {filteredCartItems.map((item, idx) => {
            const giftItemDetail = giftItemListData?.find((gi) => gi._id === item.itemId);
            const sig = optionSignature(item.selectedOptions);
            return (
              <GiftCheckoutItemCard
                key={`${item.itemId}-${idx}`}
                itemName={item.itemName}
                itemImage={item.itemImage}
                priceRetail={item.priceRetail}
                quantity={item.quantity}
                minOrderQuantity={item.minOrderQuantity}
                orderUnit={item.orderUnit}
                selectedOptions={item.selectedOptions}
                additionalOptions={
                  giftItemDetail?.giftItemInfo?.additionalOptions as
                    | AdditionalOption<Image>[]
                    | undefined
                }
                onChangeOptions={(next) => changeOptions(item.itemId, sig, next)}
                onIncrease={() => increaseQuantity(item.itemId, sig, item.orderUnit)}
                onDecrease={() =>
                  decreaseQuantity(
                    item.itemId,
                    sig,
                    item.minOrderQuantity,
                    item.orderUnit,
                  )
                }
                itemType={giftItemDetail?.giftItemInfo?.type}
                deliveryMode={deliveryMode}
                unitSurcharge={getUnitSurcharge(
                  giftItemDetail?.giftItemInfo,
                  item.quantity,
                )}
                surchargeMinQty={
                  resolveShippingFeeConfig(giftItemDetail?.giftItemInfo)
                    .freeShippingMinQuantity
                }
              />
            );
          })}
        </div>
      </div>

      {/* 2-1. 감사 메시지 인쇄 (감사 인쇄 가능 상품 포함 시) — 주문 전체 통일 적용 */}
      {orderThanksPrint && (
        <div className="mt-2">
          <GiftThanksPrintSummary
            thanksPrint={orderThanksPrint.config}
            brandId={orderThanksPrint.brandId}
            allowedTemplateIds={orderThanksPrint.allowedTemplateIds}
            bugoId={bugoId}
            eligibleCount={orderThanksPrint.eligibleCount}
          />
        </div>
      )}

      {/* 3. 배송 방식 선택 (배송형 상품 포함 시) */}
      {hasDeliveryItem && (
        <div className="mt-2">
          <GiftDeliveryModeSelector
            deliveryMode={deliveryMode}
            onDeliveryModeChange={setDeliveryMode}
            receiverAddress={bulkReceiverAddress}
            onReceiverAddressChange={setBulkReceiverAddress}
            receiverPostalCode={bulkReceiverPostalCode}
            onReceiverPostalCodeChange={setBulkReceiverPostalCode}
            receiverAddressDetail={bulkReceiverAddressDetail}
            onReceiverAddressDetailChange={setBulkReceiverAddressDetail}
            allowIndividual={allowIndividual}
            addressButtonRef={addressButtonRef}
            savedAddress={savedAddress}
            showSavedChip={showSavedAddressChip}
            onApplySaved={applySavedAddress}
            showClear={canClearAddress}
            onClear={clearSavedAddress}
          />
        </div>
      )}

      {/* 4. 최종 결제 금액 */}
      <div className="mt-2 bg-white p-4">
        <h3 className="mb-3 font-bold">결제 금액</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">상품 금액</span>
            <span>{formatPrice(finalTotal)}원</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="font-bold">총 결제 금액</span>
            <span className="text-lg font-bold text-red-500">
              {formatPrice(finalTotal)}원
            </span>
          </div>
        </div>
      </div>

      {/* 5. 개인정보 동의 (체크 없이 표시만) */}
      <div className="mt-2 bg-white p-4">
        <GiftPrivacyNotice />
      </div>

      {/* 6. 결제 도움 안내 */}
      <div className="mt-2 bg-white">
        <GiftCallHelperBox />
      </div>

      {/* 7. 하단 고정 결제 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-3 shadow-lg">
        <div className="mx-auto max-w-lg">
          <button
            type="button"
            onClick={handlePayment}
            disabled={filteredCartItems.length === 0 || isProcessing}
            className="w-full rounded-lg bg-red-500 py-3 text-lg font-bold text-white disabled:bg-gray-300"
          >
            {isProcessing ? '결제 진행 중...' : `${formatPrice(finalTotal)}원 결제하기`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftCheckoutPage;
